import { createApi } from '@reduxjs/toolkit/query/react'
import axios from 'axios'
import _ from 'lodash'

import { Creators as AlertActions } from '../appRedux/ducks/alert'
import { Creators as authActions } from '../appRedux/ducks/auth/auth'
import store from '../appRedux/store'
import { getMessage } from '../util/IntlMessages'
import { getErrorRequestMessage } from '../util/handlerRequestUtil'

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 90000,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    Pragma: 'no-cache'
  }
})

const apiEib = axios.create({
  baseURL: process.env.REACT_APP_API_EIB_URL,
  timeout: 90000,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    Pragma: 'no-cache'
  }
})

api.interceptors.request.use(async (config) => {
  try {
    const token = getToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    return config
  } catch (error) {
    console.tron.log(error)
  }
})

apiEib.interceptors.request.use(async (config) => {
  try {
    const token = getToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    if (config.url === '/health') {
      const accessTokenExp = _.get(store.getState(), 'auth.auth.accessTokenExp')
      if (accessTokenExp && accessTokenExp * 1000 <= Date.now() + 80000) {
        const response = await refreshAuth()
        store.dispatch(authActions.refreshSuccess(response))
      }
    }

    return config
  } catch (error) {
    console.tron.log(error)
    const errorMessage = getErrorRequestMessage(error.response)
    store.dispatch(AlertActions.openAlert(errorMessage))
    throw error.response
  }
})

export function getHealth() {
  return apiEib.get('/health').then((result) => result.data)
}

export const reduxBaseRequest = async ({ url, method, body, ...rest }) => {
  try {
    const response = await api.request({ url, method, data: body, ...rest })
    return response
  } catch (error) {
    getErrorRequestMessage(error.response)
    return { error }
  }
}

export const soyshareApi = createApi({
  reducerPath: 'soyshareApi',
  baseQuery: reduxBaseRequest,
  endpoints: () => ({})
})

export function getApiUrl() {
  return process.env.REACT_APP_API_URL
}

export function getToken() {
  return _.get(store.getState(), 'auth.auth.token')
}

export async function requestBackgroundReport(params) {
  return apiEib.post(`/reports/background`, params).then((result) => result.data)
}

export async function downloadFile({ fileKey, fileName }) {
  try {
    const { url } = await apiEib.post(`/file-storage/download-url`, { fileKey }).then((response) => response.data)

    const response = await axios({
      url,
      method: 'GET',
      responseType: 'blob'
    })

    const downloadLink = document.createElement('a')
    downloadLink.href = window.URL.createObjectURL(response.data)
    downloadLink.setAttribute('download', fileName)
    document.body.appendChild(downloadLink)
    downloadLink.click()
    document.body.removeChild(downloadLink)
    window.URL.revokeObjectURL(downloadLink.href)
  } catch {
    store.dispatch(AlertActions.openAlert({ message: getMessage('notification.error.fileDownload') }))
  }
}

export function auth(data) {
  return apiEib.post('/auth/login', data, { withCredentials: true }).then((result) => result.data)
}

export function authAzure(azureAccessToken) {
  return apiEib
    .post(
      '/auth/login/azure',
      {},
      {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${azureAccessToken}`
        }
      }
    )
    .then((result) => result.data)
}

export function refreshAuth() {
  return apiEib.get('/auth/refresh', { withCredentials: true }).then((result) => result.data)
}

export function passwordRecovery(data) {
  return apiEib.post('/auth/recover-password', data).then((result) => result.data)
}

export function passwordReset(data) {
  return apiEib.post('/auth/reset-password', data).then((result) => result.data)
}

export function getDistrictById(id) {
  return apiEib.get(`/districts/${id}`).then((result) => result.data)
}

export function getRegionsById(id) {
  return apiEib.get(`/regions/${id}`).then((result) => result.data)
}

export function getOrganizationsById(id) {
  return apiEib.get(`/organizations/${id}`).then((result) => result.data)
}

export function getRegions() {
  return apiEib.get(`/regions`).then((result) => result.data)
}

export function getCategory(id) {
  return apiEib.get(`/eib-categoria/${id}`).then((result) => result.data)
}

export function getCategories(data) {
  return apiEib
    .get('/eib-categoria', {
      params: {
        ...data
      }
    })
    .then((result) => result.data)
}

export function listCategories(params) {
  return apiEib.get('/eib-categoria/list', { params }).then((result) => result.data)
}

export function listCategoriesDropDown(params) {
  return apiEib.get('/eib-categoria/dropdown/list', { params }).then((result) => result.data)
}

export function getCategoriesByLocation(locationId) {
  return apiEib.get(`/eib-categoria/location/${locationId}`).then((result) => result.data)
}

export function countParticipantByCategory(id, params) {
  return apiEib.get(`/eib-categoria/${id}/customers`, { params }).then((result) => result.data)
}

export function searchCustomersByCategoryAction(id, params) {
  return apiEib.get(`/eib-categoria-acoes/${id}/search/customers`, { params }).then((result) => result.data)
}

export function countParticipantByCategoryAction(id, params) {
  return apiEib.get(`/eib-categoria-acoes/${id}/customers`, { params }).then((result) => result.data)
}

export function countAreaByCategory(id, params) {
  return apiEib.get(`/eib-categoria/${id}/area`, { params }).then((result) => result.data)
}

export function countExecutedByLocationAndSafra(locationId, idSafra) {
  return apiEib.get(`/eib-locations-plain/countactionexecuted/${locationId}/${idSafra}`).then((result) => result.data)
}

export function countTaxaExecucaoByCategory(idCategoria, idSafra) {
  return apiEib.get(`/eib-categoria-acoes/${idCategoria}/taxa/${idSafra}`).then((result) => result.data)
}

export function getAreaByCategoryAction(id, params) {
  return apiEib.get(`/eib-categoria-acoes/${id}/area`, { params }).then((result) => result.data)
}

export function getActionAcoes() {
  return apiEib.get(`/eib-action-acoes`).then((result) => result.data)
}

export function listMacroRegions(data) {
  return api.get('/city-states', data).then((result) => result.data)
}

export function listMicroRegions(data) {
  return api
    .get('/city-states', {
      params: {
        ...data
      }
    })
    .then((result) => result.data)
}

export function listCities(params) {
  return apiEib.get('/city-states', { params }).then((result) => result.data)
}

export function listCitiesByState(params) {
  return apiEib.get('/city-states', { params }).then((result) => result.data)
}

export function listCitiesDropDown(params) {
  return apiEib.get('/city-states/dropdown/list', { params }).then((result) => result.data)
}

export function listStates(params) {
  return apiEib.get('/city-states/states/dropdown/list', { params }).then((result) => result.data)
}

export function listStatesDropDown(data) {
  return apiEib.get('/states', data).then((result) => result.data)
}

export function listStatesDropdown() {
  return apiEib.get('city-states/states').then((result) => result.data)
}

export function listCustomerTypes(data) {
  return apiEib
    .get('/customer-types', {
      params: {
        ...data
      }
    })
    .then((result) => result.data)
}

export function listCustomerTypesDropdown(params) {
  return apiEib.get('/customer-types/dropdown/list', { params }).then((result) => result.data)
}

export function retrieveCustomerTypesByid(id) {
  return apiEib.get(`/customer-types/${id}`).then((result) => result.data)
}

export function retrieveCustomerTypes(id) {
  return apiEib.get(`/customer-types/${id}`).then((result) => result.data)
}

export function storeCustomerTypes(data) {
  return apiEib.post('/customer-types/', data).then((result) => result.data)
}

export function updateCustomerTypes(id, data) {
  return apiEib.put(`/customer-types/${id}`, data).then((result) => result.data)
}

export function retrieveMembersLink(id, data) {
  return api
    .get(`/members/links/${id}`, {
      params: {
        ...data
      }
    })
    .then((result) => result.data)
}

export function listMembersLink(data) {
  return apiEib
    .get(`/member-links`, {
      params: {
        ...data
      }
    })
    .then((result) => result.data)
}

export function storeMembersLink(data) {
  return apiEib.post('/member-links', data).then((result) => result.data)
}

export function updateMembersLink(id, data) {
  return apiEib.put(`/member-links/${id}`, data).then((result) => result.data)
}

export function removeMembersLink(id) {
  return apiEib.delete(`/member-links/${id}`).then((result) => result.data)
}

export function destroyCustomerTypes(id, data) {
  return api.delete(`/customer-types/${id}`, data).then((result) => result.data)
}

export function retrieveCustomer(id) {
  return apiEib.get(`/customers/${id}/relations`).then((result) => result.data)
}

export function storeCustomer(data) {
  return apiEib.post('/customers', data).then((result) => result.data)
}

export function updateCustomer(id, data) {
  return apiEib.put(`/customers/${id}`, data).then((result) => result.data)
}

export function destroyCustomer(id, data) {
  return apiEib.delete(`/customers/${id}`, data).then((result) => result.data)
}

export function createCustomerFocal(data) {
  return apiEib.post('/customer-focals', data).then((result) => result.data)
}

export function deleteCustomerFocal(data) {
  return apiEib.delete('/customer-focals', { data }).then((result) => result.data)
}

export function listLocationsByCustomerAndMembers(customerId, memberId) {
  return apiEib
    .get(`/locations/getlocationsbycustomer/${customerId}/member/${memberId}  `)
    .then((result) => result.data)
}

export function listCustomerLocations(id, params) {
  return apiEib.get(`/locations/customer/${id}`, params).then((result) => result.data)
}

export function documentTypesDropdown() {
  return apiEib.get('/document-type/dropdown/list').then((result) => result.data)
}

export function listDocumentTypes() {
  return apiEib.get('/document-type').then((result) => result.data)
}

export function listDocumentTypesById(id) {
  return apiEib.get(`/document-type/${id}`).then((result) => result.data)
}

export function createCategory(data) {
  return apiEib.post('/eib-categoria', data).then((result) => result)
}

export function updateCategory(id, data) {
  return apiEib.put(`/eib-categoria/${id}`, data).then((result) => result)
}

//  Grupos e  Alternativos

export function listCustomerGroup() {
  return apiEib.get(`/customer-group`).then((result) => result.data)
}

export function listCustomerGroupById(id) {
  return apiEib.get(`/customer-group/${id}`).then((result) => result.data)
}

export function listCustomerGroupByCustomerId(customerId) {
  return apiEib.get(`/customer-group/customer/${customerId}`).then((result) => result.data)
}

export function createCustomerGroup(data) {
  return apiEib.post(`/customer-group`, data).then((result) => result.data)
}

export function updateCustomerGroup(id, data) {
  return apiEib.put(`/customer-group/${id}`, data).then((result) => result.data)
}

export function removeCustomerGroup(id) {
  return apiEib.delete(`/customer-group/${id}`).then((result) => result.data)
}

export function updateAlternativeDocumentsGroupName(id, currentGroup, data) {
  return apiEib.put(`/partner-documents/${id}/groups/${currentGroup}`, data).then((result) => result.data)
}

export function findAlternativesByGroupId(groupId) {
  return apiEib.get(`partner-documents/group/alternatives/${groupId}`).then((result) => result.data)
}

export function findAvailableAlternatives(groupId) {
  return apiEib.get(`partner-documents/group/available-alternatives/${groupId}`).then((result) => result.data)
}

export function updateCustomerAlternativeDocuments(id, data) {
  return apiEib.put(`/partner-documents/${id}`, data).then((result) => result.data)
}

export function updateCustomerAlternativeDocumentsActive(partnerId, data) {
  return apiEib.put(`/partner-documents/${partnerId}/active`, data).then((result) => result.data)
}

export function updateCustomerAlternativeDocumentsOriginal(id, groupId) {
  return apiEib.put(`/partner-documents/${id}/group/${groupId}`).then((result) => result.data)
}

export function createCustomerAlternativeDocuments(data) {
  return apiEib.post(`/partner-documents`, data).then((result) => result.data)
}

export function listCustomerAlternativeDocuments(customerId, data = {}) {
  return api
    .get(`/partner-documents/customer/${customerId}`, {
      params: {
        ...data
      }
    })
    .then((result) => result.data)
}

export function retrieveCustomerAlternativeDocuments(id) {
  return apiEib.get(`/partner-documents/${id}`).then((result) => result.data)
}

export function listCustomerAlternative() {
  return apiEib.get(`/partner-documents`).then((result) => result.data)
}

export function destroyAlternativeDocuments(id) {
  return apiEib.delete(`/partner-documents/${id}`).then((result) => result.data)
}

export function listAlternativeDocumentsByGroupName(id, params) {
  return apiEib.get(`/partner-documents/${id}/groups`, { params }).then((result) => result.data)
}

export function countAlternativeDocumentsByGroupId(groupId) {
  return apiEib.get(`/customer-documents/count/${groupId}`).then((result) => result.data)
}

export function createCustomerDocuments(data) {
  return apiEib.post(`/customer-documents`, data).then((result) => result.data)
}

export function removeAlternativeFromGroup(alternativeId, customerGroupId) {
  return apiEib
    .delete(`/customer-documents/alternative/${alternativeId}/group/${customerGroupId}`)
    .then((result) => result.data)
}

export function listCustomer(data) {
  return api
    .get('/customers', {
      params: {
        ...data
      }
    })
    .then((result) => result.data)
}

//Locations
export function listLocations(data) {
  return api
    .get('/locations', {
      params: {
        ...data
      }
    })
    .then((result) => result.data)
}

export function retrieveLocations(id, data) {
  return api
    .get(`/locations/${id}`, {
      params: {
        ...data
      }
    })
    .then((result) => result.data)
}

export function storeLocations(data) {
  return apiEib.post('/locations', data).then((result) => result.data)
}

export function destroyLocation(id) {
  return apiEib.delete(`/locations/${id}`).then((result) => result.data)
}

export function updateLocations(id, data) {
  return apiEib.put(`/locations/${id}`, data).then((result) => result.data)
}

export function listUsers(data) {
  return apiEib.get('/users', { data }).then((result) => result.data)
}

export async function getUserById(id) {
  return apiEib.get(`/users/${id} `).then((result) => result.data)
}

export async function getCitystateById(id) {
  return apiEib.get(`/city-states/${id} `).then((result) => result.data)
}

export function listMemberTypesDropDown(params) {
  return apiEib.get('/member-types/dropdown/list', { params }).then((result) => result.data)
}

export function listMemberTypes(params) {
  return apiEib.get('/member-types', { params }).then((result) => result.data)
}

export function listTypes(params) {
  return apiEib.get(`/location-type`, { params }).then((result) => result.data)
}

export function listLocationTypeDropdwon(params) {
  return apiEib.get(`/location-type/dropdown/list`, { params }).then((result) => result.data)
}

export function retrieveTypes(id, type) {
  return api.get(`/types/${type}/${id}`).then((result) => result.data)
}

export function storeTypes(data, type) {
  return api.post(`/types/${type}`, data).then((result) => result.data)
}

export function updateTypes(id, data, type) {
  return api.put(`/types/${type}/${id}`, data).then((result) => result.data)
}

export function destroyMemberTypes(id, data) {
  return api.delete(`/types/member/${id}`, data).then((result) => result.data)
}

export function listOrganizationTypes(data) {
  return api
    .get('/organization-types', {
      params: {
        ...data
      }
    })
    .then((result) => result.data)
}

export function retrieveOrganizationTypes(id) {
  return api.get(`/organization-types/${id}`).then((result) => result.data)
}

export function storeOrganizationTypes(data) {
  return api.post('/organization-types/', data).then((result) => result.data)
}

export function updateOrganizationTypes(id, data) {
  return api.put(`/organization-types/${id}`, data).then((result) => result.data)
}

export function destroyOrganizationTypes(id, data) {
  return api.delete(`/organization-types/${id}`, data).then((result) => result.data)
}

export function listContacts(params) {
  return apiEib.get('/contacts', { params }).then((result) => result.data)
}

export function retrieveContacts(id) {
  return api.get(`/contacts/${id}`).then((result) => result.data)
}

export function bulkStoreContacts(data) {
  return api.post('/contacts/bulk', data).then((result) => result.data)
}

export function updateContacts(id, data) {
  return apiEib.put(`/contacts/${id}`, data).then((result) => result.data)
}

export function destroyContacts(id, data) {
  return apiEib.delete(`/contacts/${id}`, data).then((result) => result.data)
}

export async function getForecastsType(params) {
  return apiEib.get(`/forecast-type/list/dropdown`, { params }).then((result) => result.data)
}

export function retrieveForecasts(customerId, params = {}) {
  return apiEib.get(`/forecasts/forecastsbycustomer/${customerId}`, { params }).then((result) => result.data)
}

export function storeForecasts(data) {
  return apiEib.post('/forecasts/deprecate', data).then((result) => result.data)
}

export function updateForecasts(id, data) {
  return apiEib.put(`/forecasts/${id}`, data).then((result) => result.data)
}

export function updateForecastItems(id, data) {
  return apiEib.put(`/forecast-items/${id}`, data).then((result) => result.data)
}

export function updateForecastCommercialProcessesItem(id, data) {
  return apiEib.put(`/forecast-commercial-processes-item/${id}`, data).then((result) => result.data)
}

export function checkForecastAction(data) {
  return apiEib.post('/forecasts/actions-check', data).then((result) => result.data)
}

export function getForecastReferencePeriodById(id) {
  return apiEib.get(`/forecasts/reference-periods/${id}`).then((result) => result.data)
}

export function listCustomers(data) {
  return api
    .get('/customers', {
      params: {
        ...data
      }
    })
    .then((result) => result.data)
}

export function listCustomersViewAgricultores(params) {
  return apiEib
    .get('/customers/viewagricultores/list', {
      params
    })
    .then((result) => result.data)
}

/**
 * Used at/customers/list
 * @param {*} data
 * @returns
 */
export function listCustomersIndex(data) {
  return api
    .get('/customers/list', {
      params: {
        ...data
      }
    })
    .then((result) => result.data)
}

export function listCustomersByRegionOrDistrict(data) {
  return api
    .get('/customers/regionordistrict', {
      params: {
        ...data
      }
    })
    .then((result) => result.data)
}

export function listOrganizations(data) {
  return apiEib.get('/organizations', data).then((result) => result.data)
}

export function listOrganizationsDropDown(params) {
  return apiEib.get('/organizations/dropdown/list', { params }).then((result) => result.data)
}

export function listBrands(data) {
  return api
    .get('/organizations/brands', {
      params: {
        ...data
      }
    })
    .then((result) => result.data)
}

export function listBusinessAreas(data) {
  return api
    .get('/organizations/business-areas', {
      params: {
        ...data
      }
    })
    .then((result) => result.data)
}

export function advanceWalletStatus(id, nextStatus) {
  return apiEib.put(`/districts/${id}/advance-status`, { idStatus: nextStatus }).then((result) => result.data)
}

export function regressWalletStatus(id, nextStatus) {
  return apiEib.put(`/districts/${id}/regress-status`, { idStatus: nextStatus }).then((result) => result.data)
}

export function countPlannedActionsByLocation(locationId) {
  return apiEib.get(`/actions/planned/location/${locationId}`).then((result) => result.data)
}

export function countExecutedActionsByLocation(locationId) {
  return apiEib.get(`/actions/executed/location/${locationId}`).then((result) => result.data)
}

export function deleteLocationPlain(locationId) {
  return apiEib.delete(`/eib-locations-plain/${locationId}`).then((result) => result.data)
}

export function getAllSafra() {
  return apiEib.get(`/safra`).then((result) => result.data)
}

export function getSafraAtual() {
  return apiEib.get(`/safra/atual`).then((result) => result.data)
}

export function getPreviousSafra() {
  return apiEib.get(`/safra/previous`).then((result) => result.data)
}

export function getAreaFromLocation(locationId, params) {
  return apiEib.get(`/locations/${locationId}/area/total`, { params }).then((result) => result.data)
}

export function listDistrict(params) {
  return apiEib.get('/districts', { params }).then((result) => result.data)
}

export function listDistrictDropDown(params) {
  return apiEib.get('/districts/dropdown/list', { params }).then((result) => result.data)
}

export function listDistrictViewDistritos(data) {
  return apiEib.get('/districts/viewdistritos/list', data).then((result) => result.data)
}

export function listDistrictViewDistritosByRegion(id) {
  return apiEib.get(`districts/region/${id}`).then((result) => result.data)
}

export function retrieveDistrict(id) {
  return apiEib.get(`/districts/${id}`).then((result) => result.data)
}

export function storeDistrict(data) {
  return apiEib.post('/districts/', data).then((result) => result.data)
}

export function updateDistrict(id, data) {
  return apiEib.put(`/districts/${id}`, data).then((result) => result.data)
}

export function destroyDistrict(id, data) {
  return apiEib.delete(`/districts/${id}`, data).then((result) => result.data)
}

export function listRegionLinks(params) {
  return apiEib.get('/regions/districts/list', { params }).then((result) => result.data)
}

export function listEibCarteiraEleitos(params) {
  return apiEib.get('/eib-carteira-eleitos/list', { params }).then((result) => result.data)
}

export function listEibCarteiraEleitosAvailableLocations(id, params) {
  return apiEib.get(`/eib-carteira-eleitos/${id}/locations/available`, { params }).then((result) => result.data)
}

export function regionLinksPdfByDistrict(districtId, memberId) {
  return apiEib
    .get(`/region-links/district/${districtId}/member/${memberId}/pdf`, { responseType: 'blob' })
    .then((result) => {
      const url = window.URL.createObjectURL(result.data)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'Relatório.pdf')
      link.click()
    })
}

export function regionLinksByCustomer(customerId) {
  return apiEib.get(`/region-links/customer/${customerId} `).then((result) => result.data)
}

export function regionLinksByDistrict(districtId) {
  return apiEib.get(`/region-links/district/${districtId} `).then((result) => result.data)
}

export function listRegion(params) {
  return apiEib.get('/regions', { params }).then((result) => result.data)
}

export function listRegionViewRegions(params) {
  return apiEib.get('/regions/viewregions/list', { params }).then((result) => result.data)
}

export function retrieaveRegions(params) {
  return apiEib.get('/regions', { params }).then((result) => result.data)
}

export function listRegionDropDown(params) {
  return apiEib.get('/regions/dropdownlist', { params }).then((result) => result.data)
}

export function retrieveRegion(id) {
  return api.get(`/regions/${id}`).then((result) => result.data)
}

export function storeRegion(data) {
  return apiEib.post('/regions', data).then((result) => result.data)
}

export function updateRegion(id, data) {
  return apiEib.put(`/regions/${id}`, data).then((result) => result.data)
}

export function destroyRegion(id, data) {
  return api.delete(`/regions/${id}`, data).then((result) => result.data)
}

export function listReferencePeriod(data) {
  return api
    .get('/safra', {
      params: {
        ...data
      }
    })
    .then((result) => result.data)
}

export function listSafra(params) {
  return apiEib.get('/safra', { params }).then((result) => result.data)
}

export function listSafraDropdown(params) {
  return apiEib.get('/safra/dropdown', { params }).then((result) => result.data)
}

export async function getSafraById(id) {
  return apiEib.get(`/safra/${id} `).then((result) => result.data)
}

export async function getLocationsByCustomer(id, params) {
  return apiEib.get(`/locations/customer/${id}`, { params }).then((result) => result.data)
}

export async function getLocationsWithContactsByCustomer(id, params) {
  return apiEib.get(`/locations/customer/${id}/contacts`, { params }).then((result) => result.data)
}

export async function getDistrictsByLocations(id) {
  return apiEib.get(`/districts/${id}`).then((result) => result.data)
}

export function listEibConfigCalendarios() {
  return apiEib.get(`/eib-config-calendarios/list`).then((result) => result.data)
}

export function storeEibConfigCalendarios(data) {
  return apiEib.post('/eib-config-calendarios', data).then((result) => result)
}

export async function getCategoriesSearch(data) {
  return apiEib
    .get(`eib-categoria/search/category`, {
      params: {
        ...data
      }
    })
    .then((result) => result.data)
}

export async function getCategoryActionsByCategory(id, data) {
  return apiEib
    .get(`/eib-categoria-acoes/category/${id}`, {
      params: {
        ...data
      }
    })
    .then((result) => result.data)
}

export function retrieveEibCarteiraEleitosByDistrict(id) {
  return apiEib.get(`eib-carteira-eleitos/district/${id}`).then((result) => result.data)
}

export async function getDataTypes() {
  return apiEib.get(`/eib-tipos-carga`).then((result) => result.data)
}

export async function getDataTypesId(id) {
  return apiEib.get(`/eib-tipos-carga/${id}`).then((result) => result.data)
}

export function getItemCargaDados(id) {
  return apiEib.get(`/eib-item-carga-dados/${id}} `).then((result) => result.data)
}

export function getItemCargaDadosByIdCarga(params) {
  return apiEib.get(`/eib-item-carga-dados`, { params }).then((result) => result.data)
}

export function findAllItemCargaDados() {
  return apiEib.get(`/eib-item-carga-dados`).then((result) => result.data)
}

export async function getStatusLoad(params) {
  return apiEib.get(`/eib-status`, { params }).then((result) => result.data)
}

export async function getDataLoadByType(id) {
  return apiEib.get(`/eib-carga-dados/type/${id} `).then((result) => result.data)
}

export async function getDataLoadById(id) {
  return apiEib.get(`/eib-carga-dados/${id} `).then((result) => result.data)
}

export async function getDataLoad(params) {
  return apiEib.get(`/eib-carga-dados/viewcargadados/list`, { params }).then((result) => result.data)
}

export async function downloadDataLoadTemplateByMode({ visitTypeCode, mode, userId, safraId }) {
  const response = await apiEib.get('/data-load/export-template', {
    params: { visitTypeCode, mode, userId, safraId },
    responseType: 'blob'
  })

  const blob = new Blob([response.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  })

  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `data-load-template-${mode}.xlsx`
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.URL.revokeObjectURL(url)

  return response
}

export const createUploadUrl = () => api.post('/files/upload-url')

export const createDownloadUrl = (fileKey) => api.post('/files/download-url', { fileKey })

export const putToPresignedUrl = (url, file) =>
  fetch(url, { method: 'PUT', mode: 'cors', credentials: 'omit', headers: { 'Content-Type': file.type }, body: file })

export const startDataLoad = ({ dataTypeId, fileKey, originalName, visitTypeCode, mode, safraId }) =>
  api.post('/data-load/actions/upload-xlsx', {
    dataTypeId,
    fileKey,
    originalName,
    visitTypeCode,
    mode,
    safraId
  })

export const replaceDataLoad = ({ id, dataTypeId, fileKey, originalName, visitTypeCode, mode, safraId }) =>
  api.put(`/data-load/actions/upload-xlsx/${Number(id)}`, {
    dataTypeId,
    fileKey,
    originalName,
    visitTypeCode,
    mode,
    safraId
  })

export const getLatestDataLoad = ({ dataTypeId, originalName }) =>
  api.get('/data-load/last', { params: { dataTypeId, originalName } }).then((r) => r.data)

export const getQueueJob = (jobId) => api.get(`/queue-jobs/${jobId}`)

export const cancelQueueJob = (jobId) => api.put(`/queue-jobs/${jobId}/cancel`)

export const getDataLoadActionGD = () => api.get('/data-load').then((r) => r.data)

export const getDataLoadActionsId = (id) => api.get(`/data-load/${Number(id)}`).then((r) => r.data)

export const downloadDataLoadErrorsXlsx = (id) => api.get(`/data-load/${Number(id)}/errors`, { responseType: 'blob' })

export async function getPreviewFromFileDataLoad(file, idtipocarga) {
  let formData = new FormData()
  formData.append('file', file, file.name)
  formData.append('idtipocarga', idtipocarga)
  const config = {
    headers: {
      'content-type': 'multipart/form-data;'
    }
  }
  return apiEib.post(`/eib-carga-dados/file/preview/`, formData, config).then((result) => result.data)
}

export async function saveDataLoadFile(file, idtipocarga) {
  let formData = new FormData()
  formData.append('file', file, file.name)
  formData.append('idtipocarga', idtipocarga)
  const config = {
    headers: {
      'content-type': 'multipart/form-data;'
    }
  }
  return apiEib.post(`/eib-carga-dados/`, formData, config).then((result) => result.data)
}

export function retrieveReferencePeriod(id) {
  return api.get(`/safra/${id} `).then((result) => result.data)
}

export function storeReferencePeriod(data) {
  return api.post('/safra/', data).then((result) => result.data)
}

export function createSafra(data) {
  return apiEib.post('/safra/', data).then((result) => result.data)
}

export function updateSafra(id, data) {
  return apiEib.put(`/safra/${id}`, data).then((result) => result.data)
}

export function updateReferencePeriod(id, data) {
  return api.put(`/safra/${id} `, data).then((result) => result.data)
}

export function destroyReferencePeriod(id, data) {
  return api.delete(`/safra/${id} `, data).then((result) => result.data)
}

export function listCustomerLinks(data) {
  return api
    .get('/customers', {
      params: {
        ...data
      }
    })
    .then((result) => result.data)
}

export function removeCustomerFromWalletByList(data) {
  return api.delete(`/region-links/removewalletbylist`, data).then((result) => result.data)
}

export function deleteRegionLink(id) {
  return apiEib.delete(`/region-links/${id}`).then((result) => result.data)
}

export function listCustomerMembers(customerId) {
  return apiEib.get(`/members/getmembersbycustomer/${customerId}`).then((result) => result.data)
}

export function listMembers(params) {
  return apiEib.get('/members', { params }).then((result) => result.data)
}

export function version() {
  return api.get('/version').then((result) => result.data)
}

export function versionEib() {
  return apiEib.get('/version').then((result) => result.data)
}

export function supportContacts() {
  return apiEib.get('/support-contacts').then((result) => result.data)
}

export function getCepData(zipCode) {
  return axios.get(`https://viacep.com.br/ws/${zipCode}/json/`).then((result) => result.data)
}

export function getCoordinatesFromCep(query) {
  return axios
    .get(`https://nominatim.openstreetmap.org/search?q=${query}&format=json&limit=1`, {
      headers: { 'Accept-Language': 'pt-BR' }
    })
    .then((result) => result.data)
}

export function uploadCustomerImportationFile(file) {
  return api.post('/imports/customers', file).then((result) => result.data)
}

export function uploadLocationImportationFile(file) {
  return api.post('/imports/locations', file).then((result) => result.data)
}

export function uploadDistrictImportationFile(file) {
  return api.post('/imports/districts', file).then((result) => result.data)
}

export function uploadRegionImportationFile(file) {
  return api.post('/imports/regions', file).then((result) => result.data)
}

export function uploadMembersImportationFile(file) {
  return api.post('/imports/members', file).then((result) => result.data)
}

export function downloadModel(model) {
  return api.get(`/imports/download/${model}`).then((result) => result.data)
}

// export function retrieveMember(id, data) {
//   return apiEib.get(`/members/${id}`, data).then(result => result.data)
// }

export function retrieveMember(id) {
  return apiEib.get(`/members/${id}`).then((result) => result.data)
}

export function retrieveMemberFull(id) {
  return apiEib.get(`/members/fullmember/${id}`).then((result) => result.data)
}

export function storeMember(data) {
  return apiEib.post('/members', data).then((result) => result.data)
}

export function updateMember(id, data) {
  return apiEib.put(`/members/${id}`, data).then((result) => result.data)
}

export function destroyMember(id, data) {
  return apiEib.delete(`/members/${id}`, data).then((result) => result.data)
}

export function listMember(data) {
  return apiEib
    .get('/members', {
      params: {
        ...data
      }
    })
    .then((result) => result.data)
}

export function listProfiles(data) {
  return apiEib
    .get('/profiles', {
      params: {
        ...data
      }
    })
    .then((result) => result.data)
}

export function listProfilesDropDown(params) {
  return apiEib.get('/profiles/list/dropdown', { params }).then((result) => result.data)
}

export function retrieveProfiles(id, data) {
  return api
    .get(`/profiles/${id}`, {
      params: {
        ...data
      }
    })
    .then((result) => result.data)
}

export function storeProfiles(data) {
  return apiEib.post('/profiles', data).then((result) => result.data)
}

export function updateProfiles(id, data) {
  return apiEib.put(`/profiles/${id}`, data).then((result) => result.data)
}

export function retrieveActions(id) {
  return apiEib.get(`/actions/${id}`).then((result) => result.data)
}

export function getFieldsByActionType(actionTypeId) {
  return api.get(`/actions/fields/${actionTypeId}`).then((result) => result.data)
}

export function storeActions(data) {
  return apiEib.post('/actions', data).then((result) => result.data)
}

export function updateActions(id, data) {
  return apiEib.put(`/actions/${id}`, data).then((result) => result.data)
}

export function destroyActions(id, data) {
  return apiEib.delete(`/actions/${id}`, data).then((result) => result.data)
}

export function getActionImagesByCustomer(id) {
  return apiEib.get(`/actions/customer/${id}/images`).then((result) => result.data)
}

export function getTotalActionsByCustomerAndStatus(id, data) {
  return apiEib
    .get(`/actions/customer/${id}`, {
      params: {
        ...data
      }
    })
    .then((result) => result.data)
}

// Configurações
export function getAllParameters(data) {
  return apiEib
    .get(`/parameters`, {
      params: {
        ...data
      }
    })
    .then((result) => result.data)
}

export function findParametersByKey(key) {
  return apiEib.get(`/parameters/key/${key}`).then((result) => result.data)
}

export function createParameters(data) {
  return apiEib.post(`/parameters`, data).then((result) => result.data)
}

export function updateParameters(id, data) {
  return apiEib.put(`/parameters/${id}`, data).then((result) => result.data)
}

export function removeParameters(id) {
  return apiEib.delete(`/parameters/${id}`).then((result) => result.data)
}

//Actions-Status-routes
export function listActionStatus(params) {
  return apiEib.get('/action-status', { params }).then((result) => result.data)
}

export function listActionStatusDropdown(params) {
  return apiEib.get('/action-status/dropdown/list', { params }).then((result) => result.data)
}

export function getActionStatusById(id) {
  return apiEib.get(`/action-status/${id}`).then((result) => result.data)
}

//Actions-types-routes
export function listActionTypes(params) {
  return apiEib.get('/action-types', { params }).then((result) => result.data)
}

export function listActionTypesWithInactives(params) {
  return apiEib.get('/action-types/with-inactives', { params }).then((result) => result.data)
}

export function listActionTypesDropdown(params) {
  return apiEib.get('/action-types/dropdown/list', { params }).then((result) => result.data)
}

export function getActionTypesDropdown(params) {
  return apiEib.get('/action-types/dropdown/list', { params }).then((result) => result.data)
}

export function findFieldsToFill(data) {
  return api.post('/actions/fields-to-fill', data).then((result) => result.data)
}

export function retrieveActionTypes(id, data) {
  return apiEib
    .get(`/action-types/${id}`, {
      data
    })
    .then((result) => result.data)
}

export function storeActionTypes(data) {
  return apiEib.post('/action-types', data).then((result) => result.data)
}

export function updateActionTypes(id, data) {
  return apiEib.put(`/action-types/${id}`, data).then((result) => result.data)
}

export function destroyActionTypes(id, data) {
  return apiEib.delete(`/action-types/${id}`, data).then((result) => result.data)
}

//Action template items
export function retrieveActionTemplateItems(id, data) {
  return api
    .get(`/action-template-items/${id}`, {
      params: {
        ...data
      }
    })
    .then((result) => result.data)
}

//Actions templates routes
export function listActionTemplates(data) {
  return api.get('/action-templates', {
    params: {
      ...data
    }
  })
}

export function retrieveActionTemplates(id, data) {
  return api
    .get(`/action-templates/${id}`, {
      params: {
        ...data
      }
    })
    .then((result) => result.data)
}

export function storeActionTemplates(data) {
  return api.post('/action-templates', data).then((result) => result.data)
}

export function updateActionTemplates(id, data) {
  return api.put(`/action-templates/${id}`, data).then((result) => result.data)
}

export function destroyActionTemplates(id, data) {
  return api.delete(`/action-templates/${id}`, data).then((result) => result.data)
}

export function generationActionTemplates(id, data) {
  return api.post(`/action-templates/${id}/generate-action`, data).then((result) => result.data)
}

export function destroyActionTemplateItem(id, data) {
  return api.delete(`/action-template-items/${id}`, data).then((result) => result.data)
}

//Report routes
export function listReports(data) {
  return api.get('/reports', { params: { ...data } }).then((result) => result.data)
}

//Actions routes
export function listActions(data) {
  return apiEib.get('/actions', { params: { ...data } }).then((result) => result.data)
}

export function listActionsViewVisitas(params) {
  return apiEib
    .get('/actions/viewvisitas/list', {
      params
    })
    .then((result) => result.data)
}

export function getTimeline(data) {
  return apiEib.get('/actions/timeline', { params: { ...data } }).then((result) => result.data)
}

export function getActionsReport(params = {}) {
  return api.get(`/actions/reports`, { params }).then((result) => result.data)
}

export function shareAction(actionId) {
  return api.post(`/actions/${actionId}/share`)
}

export function listDynamicField(actionTypeId) {
  return apiEib.get(`/action-custom-fields/getcustomfieldsbyactiontypeid/${actionTypeId}`).then((result) => result.data)
}

export function createForm(data) {
  return api.post(`/forms/create`, data).then((result) => result.data)
}

export function listCustomFieldsJSON() {
  return apiEib.get(`/form-fields/list-json-report`).then((result) => result.data)
}

export function listCustomFields(id) {
  return apiEib.get(`/form-fields/form-id/${id}`).then((result) => result.data)
}

export function createCustomField(data) {
  return apiEib.post(`/form-fields`, data).then((result) => result.data)
}

export function updateCustomFieldJSON(id, reportGroup, isJsonText) {
  return apiEib
    .put(`/form-fields/id/${id}/reportgroup/${reportGroup}/isshow/${isJsonText}`)
    .then((result) => result.data)
}

export function updateCustomField(id, data) {
  return apiEib.put(`/form-fields/id/${id}`, data).then((result) => result.data)
}

export function saveFieldsOrder(data) {
  return apiEib.put(`/form-fields/reorder`, data).then((result) => result.data)
}

export async function updateFormFieldRelationships(data) {
  return apiEib.put(`/form-fields/relationships`, data).then((result) => result.data)
}

export async function updateSelectOptionsRelationships(data) {
  return apiEib.put(`/form-fields/relationships/select-options`, data).then((result) => result.data)
}

export async function retrieveSelectOptionsRelationships(id) {
  return apiEib.get(`/form-fields/relationships/select-options/option/${id}`).then((result) => result.data)
}

export function listFormFields(filter) {
  return api
    .get('/forms/list', {
      params: {
        ...filter
      }
    })
    .then((result) => result.data)
}

export function retrieveCustomFields(id) {
  return api.get(`/forms/fields/${id}`).then((result) => result.data)
}

export function updateOrderCustomFields(data) {
  return api.put(`/forms/fields/update-rows`, data).then((result) => result.data)
}

export function listCustomFieldsTypes() {
  return api.get('forms/fields/types').then(({ data }) => data)
}

export function listCustomTables(selectedTable) {
  const endpoint = selectedTable ? `forms/tables/${selectedTable}` : 'forms/tables'
  return api.get(endpoint).then(({ data }) => data)
}

export function getFormsById(filter) {
  return apiEib
    .get('/forms', {
      params: {
        ...filter
      }
    })
    .then((result) => result.data)
}

export function listCustomTypes(customType) {
  return api
    .get(`${customType}-types`, {
      params: {
        ignorePagination: true
      }
    })
    .then(({ data }) => data)
}

export function storeCustomFields(data) {
  return api.post('/forms', data).then((result) => result.data)
}

//Customer-wallet
export function retrieveCustomerWalletsByDistict(districtId, data) {
  return api
    .get(`/region-links/${districtId}`, {
      params: { ...data }
    })
    .then((result) => result.data)
}

// export function getOnlyCustomersThatCanBeAddedOnWallet(data) {
//   return api.get(`/region-links/viewRegionLinks`, {
//     ...data
//   })
// }

export function getOnlyCustomersThatCanBeAddedOnWallet(data) {
  return apiEib.get(`/region-links/viewRegionLinks`, {
    ...data
  })
}

export function addCustomersWalletsByList(data) {
  return apiEib.post(`/region-links/`, data).then((result) => result.data)
}

//Mixed-action-types-routes
export function listActionTypesWithMembers(id, data) {
  return apiEib.get(`/action-types/${id}/members`, {
    params: {
      ...data
    }
  })
}

export function listActionTypesWithCustomers(id) {
  return apiEib.get(`/customers/getcustomersbyactiontype/${id}`).then((result) => result.data)
}

export async function uploadAttachmentEib(file) {
  return apiEib.post('/attachements', file).then((result) => result.data)
}

export function listActionTypeReportConfigs(data) {
  return api.get('/action-type-report-configs', {
    params: {
      ...data
    }
  })
}

export async function retrieveAttachment(id) {
  return apiEib.get(`attachements/${id}`).then(({ data }) => data)
}

export async function uploadAttachment(file) {
  return apiEib
    .post('/attachements', file, { headers: { 'Content-Type': 'multipart/form-data' } })
    .then((result) => result.data)
}

export async function removeAttachment(id) {
  return apiEib.delete(`/attachements/${id}`).then((result) => result)
}

export function getNotifications(id, prop) {
  return api
    .get(
      `/notifications${id ? `/${id}` : ''}?device=desktop&include=member${prop !== null ? `&page=${prop.page}` : ''}`
    )
    .then((result) => result.data)
}

export async function dashboardInfo(filtered) {
  return api
    .get('/dashboard/actions/info', {
      params: {
        ...filtered
      }
    })
    .then(({ data }) => data)
}

export async function dashboardActionsBar(filtered) {
  return api
    .get('/dashboard/actions/bar', {
      params: {
        ...filtered
      }
    })
    .then(({ data }) => data)
}

export async function dashboardActionsBarDistrictRegion(filtered) {
  return api
    .get('/dashboard/actions/bar-district', {
      params: {
        ...filtered
      }
    })
    .then(({ data }) => data)
}

export async function dashboardActionsLine(filtered) {
  return api
    .get('/dashboard/actions/line', {
      params: {
        ...filtered
      }
    })
    .then(({ data }) => data)
}

export async function dashboardActionsColumn(filtered) {
  return api
    .get('/dashboard/actions/types', {
      params: {
        ...filtered
      }
    })
    .then(({ data }) => data)
}

export async function EibDashActionData(params) {
  return apiEib.get('/dashboard/actions', { params }).then(({ data }) => data)
}

export async function getAllEibCategoryActions(filtered) {
  return apiEib
    .get('/eib-categoria-acoes', {
      params: {
        ...filtered
      }
    })
    .then(({ data }) => data)
}

export function getCategoryAction(id) {
  return apiEib.get(`/eib-categoria-acoes/${id}`).then((result) => result.data)
}

export function createCategoryAction(data) {
  return apiEib.post('/eib-categoria-acoes', data).then((result) => result)
}

export function deleteCategoryAction(id) {
  return apiEib.delete(`/eib-categoria-acoes/${id}`).then((result) => result.data)
}

export function updateCategoryAction(id, data) {
  return apiEib.put(`/eib-categoria-acoes/${id}`, data).then((result) => result)
}

export function listEibCategoriaAcoesByCategory(id, params) {
  return apiEib.get(`/eib-categoria-acoes/list/category/${id}`, { params }).then((result) => result.data)
}

export function listEibCategoriaAcoesByCategoryDropDown(params) {
  return apiEib.get(`/eib-categoria-acoes/dropdown/list`, { params }).then((result) => result.data)
}

export async function dashboardFarmerPortal(params) {
  return api.get(`/dashboard/farmer-portal`, { params }).then((result) => result.data)
}

// Metodos de geracao de relatorios sem paginacao

export async function reportsForecast(params) {
  return apiEib.get(`/reports/forecast`, { params }).then((result) => result.data)
}

export async function reportsArrastao(params) {
  return apiEib.get(`/reports/arrastao`, { params }).then((result) => result.data)
}

export async function reportsCargaDeDados(params) {
  return apiEib.get(`/reports/carga-dados`, { params }).then((result) => result.data)
}

export async function reportsProductivity(params) {
  return apiEib.get(`/reports/productivity`, { params }).then((result) => result.data)
}

export async function reportsCadastroEleitos(params) {
  return apiEib.get(`/reports/cadastro-eleitos`, { params }).then((result) => result.data)
}

export async function generateActionPlanReports(params) {
  return apiEib.get(`/reports/action-plans`, { params }).then((result) => result.data)
}

export async function generateFarmersPortfolio(params) {
  return apiEib.get(`/reports/farmers-portfolio`, { params }).then((result) => result.data)
}

export async function generateReportActions(params) {
  return apiEib.get(`/reports/actions`, { params }).then((result) => result.data)
}

export async function generateReportActionsDetails(params) {
  return apiEib.get(`/reports/actions-details`, { params }).then((result) => result.data)
}

export async function reportsEleitosEAlternativos(params) {
  return apiEib.get(`/reports/eleitos-alternativos`, { params }).then((result) => result.data)
}

export async function reportsActions(params) {
  return api.get(`/reports/actions`, { params }).then((result) => result.data)
}

export async function reportsActionsDetail(params) {
  return api.get(`/reports/actions-detail`, { params }).then((result) => result.data)
}

// Metodos de geracao de relatorios com paginacao

export async function reportsForecastWithPagination(params) {
  return apiEib.get(`/reports/with-pagination/forecast`, { params }).then((result) => result.data)
}

export async function reportsArrastaoWithPagination(params) {
  return apiEib.get(`/reports/with-pagination/arrastao`, { params }).then((result) => result.data)
}

export async function reportsCargaDeDadosWithPagination(params) {
  return apiEib.get(`/reports/with-pagination/carga-dados`, { params }).then((result) => result.data)
}

export async function reportsCadastroEleitosWithPagination(params) {
  return apiEib.get(`/reports/with-pagination/cadastro-eleitos`, { params }).then((result) => result.data)
}

export async function generateActionPlanReportsWithPagination(params) {
  return apiEib.get(`/reports/with-pagination/action-plans`, { params }).then((result) => result.data)
}

export async function generateFarmersPortfolioWithPagination(params) {
  return apiEib.get(`/reports/with-pagination/farmers-portfolio`, { params }).then((result) => result.data)
}

export async function generateReportActionsWithPagination(params) {
  return apiEib.get(`/reports/with-pagination/actions`, { params }).then((result) => result.data)
}

export async function generateReportActionsDetailsWithPagination(params) {
  return apiEib.get(`/reports/with-pagination/actions-details`, { params }).then((result) => result.data)
}

export async function reportsEleitosEAlternativosWithPagination(params) {
  return apiEib.get(`/reports/with-pagination/eleitos-alternativos`, { params }).then((result) => result.data)
}

export async function reportsActionsWithPagination(params) {
  return api.get(`/reports/with-pagination/actions`, { params }).then((result) => result.data)
}

export async function reportsActionsDetailWithPagination(params) {
  return api.get(`/reports/with-pagination/actions-detail`, { params }).then((result) => result.data)
}

export async function getChartDataByKey() {
  // return api.get(`/charts/data/${chartKey}`, { params }).then(result => result.data)
}

export async function getDashboardChartsByKey(chartKey, params) {
  return apiEib.get(`/dashboard/charts/${chartKey}`, { params }).then((result) => result.data)
}

export async function getMonitoringOfActionsByCategory(params) {
  return apiEib.get(`/dashboard/monitoringofactionsbycategory`, { params }).then((result) => result.data)
}

export async function getEibDashboardGrid(params) {
  return apiEib.get(`/dashboard/geteibdashboardgrid`, { params }).then((result) => result.data)
}

export async function getEibBignumbers(params) {
  return apiEib.get(`/dashboard/geteibdbignumbers`, { params }).then((result) => result.data)
}

export async function createMaterial(payload) {
  return apiEib.post(`/materials`, payload).then((result) => result.data)
}

export async function updateMaterial({ id, ...payload }) {
  return apiEib.put(`/materials/${id}`, payload).then((result) => result.data)
}

export async function getMaterials(params) {
  return apiEib.get(`/materials`, { params }).then((result) => result.data)
}

export async function getMaterialsWithTecnologia() {
  return apiEib.get(`/materials/tecnologia`).then((result) => result.data)
}

export async function getMaterialsList(params) {
  return apiEib.get(`/materials/viewmaterials/list`, { params }).then((result) => result.data)
}

export async function getOneMaterial(id, params) {
  return apiEib.get(`/materials/${id}`, { params }).then((result) => result.data)
}

export async function createCultivars(payload) {
  return apiEib.post(`/cultivars`, payload).then((result) => result.data)
}

export async function getCultivarsDemandTypesDropdown(params) {
  return apiEib.get(`/cultivars-demand-type/list/dropdown/`, { params }).then((result) => result.data)
}

export async function getCultivarsDemandTypes(params) {
  return apiEib.get(`/cultivars-demand-type/`, { params }).then((result) => result.data)
}

export async function getCultivarsTypesDropdown(params) {
  return apiEib.get(`/cultivars-type/list/dropdown/`, { params }).then((result) => result.data)
}

export async function updateCultivars(id, payload) {
  return apiEib.put(`/cultivars/${id}`, payload).then((result) => result.data)
}

export async function getCultivarsByCustomer(customerId, params) {
  return apiEib.get(`/cultivars/customer/${customerId}`, { params }).then((result) => result.data)
}

export async function getCultivarsHistoryByCustomer(customerId, params) {
  return apiEib.get(`/cultivars/customer/${customerId}/history`, { params }).then((result) => result.data)
}

export function validatePin(data) {
  return api.post(`auth/validate-pin`, data).then((result) => result.data)
}

export function sendPin(data) {
  return api.post(`auth/send-pin`, data).then((result) => result)
}

export async function getBrands(params) {
  return api.get(`/brands`, { params }).then((result) => result.data)
}

export async function getBrandsDropDown(params) {
  return apiEib.get(`/brands/list/dropdown`, { params }).then((result) => result.data)
}

export async function getTechnologiesDropDown(params) {
  return apiEib.get(`/technologies/list/dropdown`, { params }).then((result) => result.data)
}

export async function getBreedersDropdown(params) {
  return apiEib.get(`/breeders/list/dropdown`, { params }).then((result) => result.data)
}

export async function getFieldTypes() {
  return apiEib.get(`/field-types`).then((result) => result.data)
}

export async function retrieveEntitlements() {
  return apiEib.get(`/entitlements`).then((result) => result.data)
}

export async function retrieveEntitlementsByProfile(params) {
  return apiEib
    .get(`/profile-entitlements`, {
      params
    })
    .then(({ data }) => data)
}

export async function retrieveEntitlementsByProfileId(profileId, params) {
  return apiEib
    .get(`/profile-entitlements/profileId/${profileId}`, {
      params
    })
    .then(({ data }) => data)
}

export async function associateProfile(data) {
  return apiEib.post(`/profile-entitlements`, data).then((result) => result.data)
}

export async function disassociateProfile(id, data) {
  return apiEib.put(`/profile-entitlements/${id}`, data).then((result) => result.data)
}

export function sendFarmReport(data) {
  return api.post(`messages/farm-report`, data).then((result) => result)
}

export function userIdentification(data) {
  return api.post(`auth/user-identification`, data).then((result) => result)
}

export async function getLocationByDocumentNumber(params) {
  return api.get(`/locations/bydocument`, { params }).then((result) => result.data)
}

export async function countLocations() {
  return api.get(`/locations/count`).then((result) => result.data)
}

export function getDistrictByCustomer(id) {
  return apiEib.get(`/districts/customer/${id}`).then((result) => result.data)
}

export function getDistrictsByRegion(id) {
  return apiEib.get(`/districts/region/${id}`).then((result) => result.data)
}

export function getAllDistricts() {
  return apiEib.get(`/districts/`).then((result) => result.data)
}

export function getRegionsByCustomer(id) {
  return apiEib.get(`/locations/customer/region/${id}`).then((result) => result.data)
}

export function getRegionsByCustomerAndMembers(customerId, membersRegionId) {
  return apiEib
    .get(`/locations/regionByMembers/customer/${customerId}/region/${membersRegionId}`)
    .then((result) => result.data)
}

export function getDistricts(id, regionId) {
  return apiEib.get(`/locations/customer/${id}/region/${regionId}`).then((result) => result.data)
}

export function getLocationByID(id) {
  return apiEib.get(`/locations/${id}`).then((result) => result.data)
}

export function updateLocationByID(id, data) {
  return apiEib.put(`/locations/${id}`, data).then((result) => result.data)
}

export function createLocationByID(data) {
  return apiEib.post(`/locations`, data).then((result) => result.data)
}

export function getLocationDropDown(regionId, districtId, customerId) {
  return apiEib
    .get(`/locations/getDropdownData/customer/${customerId}/region/${regionId}/district/${districtId}`)
    .then((result) => result.data)
}

export function getCustomer(id) {
  return apiEib.get(`/customers/${id}`).then((result) => result.data)
}

export function getCustomersSearch(data) {
  return apiEib.get(`/customers/search`, { params: { ...data } }).then((result) => result.data)
}

export function createEibLocationsPlain(data) {
  return apiEib.post('/eib-locations-plain', data).then((result) => result)
}

export function createEibActionAcoes(data) {
  return apiEib.post('/eib-action-acoes', data).then((result) => result)
}

export function updateEibActionAcoes(idActionAcoes, data) {
  return apiEib.post(`/eib-action-acoes/${idActionAcoes}`, data).then((result) => result)
}

export function exportActionPlanPdfByCustomer(customerId, data) {
  return apiEib
    .get(`/customers/${customerId}/pdf`, {
      params: { ...data },
      responseType: 'blob'
    })
    .then((result) => {
      const url = window.URL.createObjectURL(result.data)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'relatorio-plano-de-acoes.pdf')
      link.click()
    })
}

export function getRegionsDropdown(params) {
  return apiEib.get('/regions/dropdown', { params }).then((result) => result.data)
}

export function getRegionsDropdownById(id) {
  return apiEib.get(`/regions/dropdown/${id}`).then((result) => result.data)
}

export function getDistrictsDropdown(params) {
  return apiEib.get('/districts/dropdown/list', { params }).then((result) => result.data)
}

export function getMembersDropdown(params) {
  return apiEib.get('/members/dropdown/list', { params }).then((result) => result.data)
}

export function getCustomersDropdown(params) {
  return apiEib.get('/customers/dropdown/list', { params }).then((result) => result.data)
}

export function getCustomersByRegion(data) {
  return apiEib.get('/customers/findcustomersbyregion', { params: { ...data } }).then((result) => result.data)
}

export function getCustomersByDistrict(data) {
  return apiEib.get('/customers/findcustomersbydistrict', { params: { ...data } }).then((result) => result.data)
}

export function getActionTemplatesDropdown(params) {
  return apiEib.get('/action-templates/dropdown/list', { params }).then((result) => result.data)
}

export function getEibCategoriaAcaoesRegionsDropdown(idEibCategoriaAcao) {
  return apiEib.get(`eib-categoria-acoes/${idEibCategoriaAcao}/regions/dropdown`).then((result) => result.data)
}

export function getEibCategoriaAcaoesDistrictsDropdown(idEibCategoriaAcao, params) {
  return apiEib
    .get(`eib-categoria-acoes/${idEibCategoriaAcao}/districts/dropdown`, { params })
    .then((result) => result.data)
}

export function getCountActionPlannedOrExecuted(locationId, customerId) {
  return apiEib.get(`eib-locations-plain/location/${locationId}/customer/${customerId}`).then((result) => result.data)
}

export function getEibCategoriaByLocation(locationId, params) {
  return apiEib
    .get(`/eib-locations-plain/eib-categoria/location/${locationId}`, { params })
    .then((result) => result.data)
}

export function getEibCategoriaAcoesByLocation(locationId, params) {
  return apiEib
    .get(`eib-locations-plain/eib-categoria-acoes/location/${locationId}`, { params })
    .then((result) => result.data)
}

export function getEibCategoriaAcoesByLocationForAction(locationId, params) {
  return apiEib
    .get(`eib-locations-plain/eib-action-acoes/location/${locationId}`, { params })
    .then((result) => result.data)
}

export function getEibCategoriaAcoesByLocationListWithCategoyAndPlanned(locationId, params) {
  return apiEib
    .get(`eib-locations-plain/getEibCategoriaAcoesByLocationListWithCategoyAndPlanned/location/${locationId}`, {
      params
    })
    .then((result) => result.data)
}

export function findSafraByIdForActionPlan(safraId) {
  return apiEib.get(`/safra/findsafraforactionplan/${safraId}`).then((result) => result.data)
}

export function findLocationByActionId(visitId) {
  return apiEib.get(`/actions/getlocationsbyactionid/${visitId}`).then((result) => result.data)
}

export function findAcoesByLocationId(locationId, visitId) {
  return apiEib.get(`eib-action-acoes/eib-categoria-acoes/${locationId}/${visitId}`).then((result) => result.data)
}

export function deleteEibActionAcoes(actionAcoesId) {
  return apiEib.delete(`eib-action-acoes/${actionAcoesId}`).then((result) => result.data)
}

export function getEibCategoriaByLocationForAction(locationId, params) {
  return apiEib
    .get(`/eib-locations-plain/eib-categoria-action-acoes/location/${locationId}`, { params })
    .then((result) => result.data)
}

export function getEibCategoriaAcoesByLocationForActionList(locationId, visitId, params) {
  return apiEib
    .get(`/eib-locations-plain/eib-categoria-action-acoes/list/${locationId}/${visitId}`, { params })
    .then((result) => result.data)
}

export function findAcoesByLocationIdAndSafraForAction(locationId, idSafra, params) {
  return apiEib
    .get(`eib-locations-plain/eib-action-acoes/location/${locationId}/${idSafra}`, { params })
    .then((result) => result.data)
}

export function getRegionsDropDown() {
  return apiEib.get(`/regions/dropdownlist`).then((result) => result.data)
}

export function getRegionByIdDropDown(regionId) {
  return apiEib.get(`/regions/dropdown/${regionId}`).then((result) => result.data)
}

export function updateCustomFieldById(id, body) {
  return apiEib.put(`/form-fields/id/${id}`, body).then((result) => result.data)
}

export function listDocumentTypeDropDown(data) {
  return apiEib.get('/document-type/dropdown/list', data).then((result) => result.data)
}

export function listDocumentType(data) {
  return apiEib
    .get('/document-type', {
      data
    })
    .then((result) => result.data)
}

export function getReportInfoChart(customerId) {
  return apiEib.get(`/reports/info-chart/${customerId}`).then((result) => result.data)
}

export function getReportActionsChart(customerId, params) {
  return apiEib.get(`/reports/actions-chart/${customerId}`, { params }).then((result) => result.data)
}

export function getAllLocationTypes(params) {
  return apiEib
    .get('/location-type', {
      params
    })
    .then((result) => result.data)
}

export function listDocumentTypeById(id, data) {
  return apiEib.get(`/document-type/${id}`, { data }).then((result) => result.data)
}

export function getLocationTypesByID(id) {
  return apiEib.get(`/location-type/${id}`).then((result) => result.data)
}

export function updateLocationTypes(id, data) {
  return apiEib.put(`/location-type/${id}`, data).then((result) => result.data)
}

export function createLocationTypes(data) {
  return apiEib.post(`/location-type`, data).then((result) => result.data)
}

export function getAllMemberTypes(data) {
  return apiEib
    .get('/member-types', {
      params: {
        ...data
      }
    })
    .then((result) => result.data)
}

export function listMemberHasTypeByMemberId(memberId) {
  return apiEib.get(`/member-has-member-types/memberid/${memberId}`).then((result) => result.data)
}

export function getMemberTypesById(id) {
  return apiEib.get(`/member-types/${id}`).then((result) => result.data)
}

export function storeMemberTypes(data) {
  return apiEib.post('/member-types/', data).then((result) => result.data)
}

export function updateMemberTypes(id, data) {
  return apiEib.put(`/member-types/${id}`, data).then((result) => result.data)
}

export function getForms(params) {
  return apiEib.get(`/forms`, { params }).then((result) => result.data)
}

export function getTopConsultantsForDropdown(params) {
  return apiEib.get('/top-consultants-types/dropdown/list', { params }).then((result) => result.data)
}

export function getTopConsultantsList(params) {
  return apiEib.get('/top-consultants-types', { params }).then((result) => result.data)
}

export function getOneTopConsultant(id) {
  return apiEib.get(`/top-consultants-types/${id}`).then((result) => result.data)
}

export function createTopConsultantType(data) {
  return apiEib.post('/top-consultants-types', data).then((result) => result.data)
}

export function updateTopConsultantType(id, data) {
  return apiEib.put(`/top-consultants-types/${id}`, data).then((result) => result.data)
}
